/** Reuse private stored previews, with first-page range rendering as a fallback. */
import { SUPABASE_URL } from '../../utils/supabase/url';
import { getAuthHeaders } from '../../utils/supabase/client';

let active = 0;
const waiting: Array<() => void> = [];

export async function fetchNativeDocumentThumbnail(fileId: string, mimeType?: string): Promise<Blob> {
  if (!fileId.startsWith('native-file:')) throw new Error('Only Quantis storage files have bucket previews');
  // Large construction PDFs must not all download/parse together in a grid.
  await new Promise<void>((resolve) => {
    const start = () => { active += 1; resolve(); };
    if (active < 2) start(); else waiting.push(start);
  });
  try {
    const url = `${SUPABASE_URL}/functions/v1/make-server-a476273b/files/proxy/${encodeURIComponent(fileId)}`;
    const headers = await getAuthHeaders({});
    let data: ArrayBuffer | undefined;
    // The listing already identifies PDFs. Let PDF.js request only the ranges
    // needed by page one rather than buffering an entire drawing pack first.
    if (mimeType === 'application/pdf') {
      const thumbnail = await fetch(`${SUPABASE_URL}/functions/v1/make-server-a476273b/files/thumbnail/${encodeURIComponent(fileId)}`, { headers });
      if (thumbnail.ok) {
        const blob = await thumbnail.blob();
        if (!blob.type.startsWith('image/') || blob.size > 1024 * 1024) throw new Error('Invalid stored preview');
        return blob;
      }
      // Older servers and installations without the renderer still support
      // authenticated range loading. Denials must never become a fallback.
      if (thumbnail.status !== 404) throw new Error(`Could not load preview (HTTP ${thumbnail.status})`);
    } else {
      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error(`Could not load preview (HTTP ${response.status})`);
      const blob = await response.blob();
      if (blob.type.startsWith('image/')) return blob;
      if (!blob.type.includes('pdf')) throw new Error('No thumbnail available for this file type');
      data = await blob.arrayBuffer();
    }

    const pdfjs = await import('pdfjs-dist');
    if (!pdfjs.GlobalWorkerOptions.workerSrc) {
      pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).toString();
    }
    const task = pdfjs.getDocument({
      ...(data ? { data } : {
        url,
        httpHeaders: headers,
        disableAutoFetch: true,
        disableStream: true,
        rangeChunkSize: 64 * 1024,
      }),
      wasmUrl: '/pdfjs-wasm/',
    });
    try {
      const pdf = await task.promise;
      const page = await pdf.getPage(1);
      const original = page.getViewport({ scale: 1 });
      // A bounded 640px long edge stays sharp on retina without retaining sheets.
      const viewport = page.getViewport({ scale: 640 / Math.max(original.width, original.height) });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) throw new Error('Preview canvas unavailable');
      await page.render({ canvas, canvasContext: context, viewport, background: '#ffffff' }).promise;
      const image = await new Promise<Blob>((resolve, reject) => canvas.toBlob((result) => result ? resolve(result) : reject(new Error('Could not render preview')), 'image/png'));
      page.cleanup();
      canvas.width = 0;
      canvas.height = 0;
      return image;
    } finally {
      await task.destroy();
    }
  } finally {
    active -= 1;
    waiting.shift()?.();
  }
}
